import { Suspense, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { Typography } from '@mui/material';
import { CheckCircle, Schedule } from '@mui/icons-material';
import { DateFormats, practiceStateSetupRoute, ROUTING_CONFIG } from '@uplift/constants';
import { licenseAllowsStateExhibit, licenseRequiresCollaborativeAgreement } from '@uplift/pages';
import { ContextualNavigationLayout, Loader, useAlert } from '@uplift/ui-v2';
import { CalendarDateService } from '@uplift/services';
import { useCollaborativeStateDetailsQuery, useProviderCollaborativeMatchRequestsQuery, useProviderLicensesStatusQuery, useRequestCollaborativePhysicianMatchMutation, useStateExhibitTemplateQuery, useStateQuery } from '@uplift/queries';
import { License } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { useGoLiveLocked } from '../../hooks';
import { QuestionStep } from './components/QuestionStep';
import { CollabAgreementStep } from './components/CollabAgreementStep';
import { ExhibitSignatureStep } from './components/ExhibitSignatureStep';
import { FinalStep } from './components/FinalStep';

type WizardStep =
  | 'stateExhibitQuestion'
  | 'collaborationQuestion'
  | 'exhibitSign'
  | 'exhibitSigned'
  | 'collaborationUpload'
  | 'collaborationSubmitted'
  | 'needMatch';

type WizardProps = {
  license: License;
  matchRequested: boolean;
};

const PracticeStateSetupWizard = ({ license, matchRequested }: WizardProps) => {
  const history = useHistory();
  const alert = useAlert();
  const requestMatch = useRequestCollaborativePhysicianMatchMutation(license.userId, license.id);

  const { data: state } = useStateQuery(license.stateId, { suspense: true });
  const { data: collaborativeStateDetails } = useCollaborativeStateDetailsQuery(license.stateId);
  const { data: exhibitTemplate } = useStateExhibitTemplateQuery(license.userId, license.id);
  const requiresCollaborativeAgreement = licenseRequiresCollaborativeAgreement(license, collaborativeStateDetails);
  const showStateExhibitOption = licenseAllowsStateExhibit(license, collaborativeStateDetails) && !!exhibitTemplate;

  const [ step, setStep ] = useState<WizardStep>(() => {
    if (matchRequested) {
      return 'needMatch';
    }

    if (!showStateExhibitOption) {
      return 'collaborationQuestion';
    }

    return requiresCollaborativeAgreement ? 'stateExhibitQuestion' : 'exhibitSign';
  });
  const [ stateExhibitAnswer, setStateExhibitAnswer ] = useState<string | null>(null);
  const [ collaborationAnswer, setCollaborationAnswer ] = useState<string | null>(null);
  const [ exhibitExpiresAt, setExhibitExpiresAt ] = useState<string | null>(null);

  const goToPracticeStates = () => history.push(ROUTING_CONFIG.practiceStates);

  switch (step) {
    case 'stateExhibitQuestion':
      return (
        <QuestionStep
          overline="QUESTION 1 OF 2"
          title={`Do you meet ${state?.fullName}'s requirements to practice independently?`}
          subtitle={`For example: an active ${state?.shortName} APN license and the required supervised practice hours.`}
          options={[
            { value: 'independent', title: 'Yes — I qualify for independent practice', description: 'No collaborating physician needed.' },
            { value: 'collaborative', title: "No — I'll work with a collaborating physician", description: `${state?.fullName} requires a collaborative agreement.` },
          ]}
          value={stateExhibitAnswer}
          onChange={setStateExhibitAnswer}
          onContinue={() => setStep(stateExhibitAnswer === 'independent' ? 'exhibitSign' : 'collaborationQuestion')}
          onBack={goToPracticeStates}
        />
      );

    case 'collaborationQuestion':
      return (
        <QuestionStep
          overline={showStateExhibitOption ? 'QUESTION 2 OF 2' : undefined}
          title="Do you already have a collaborative agreement?"
          subtitle="We'll either use the agreement you've signed, or pair you with a supervising physician."
          options={[
            { value: 'upload', title: "Yes — I'll upload it", description: "Upload an agreement you've already signed." },
            { value: 'match', title: 'No — match me with a physician', description: 'BetterHelp pairs you with a supervising physician.' },
          ]}
          value={collaborationAnswer}
          onChange={setCollaborationAnswer}
          onContinue={async () => {
            if (collaborationAnswer === 'upload') {
              setStep('collaborationUpload');
              return;
            }

            try {
              await requestMatch.mutateAsync();
              setStep('needMatch');
            } catch (err) {
              alert.error(err.message);
            }
          }}
          onBack={() => (showStateExhibitOption ? setStep('stateExhibitQuestion') : goToPracticeStates())}
        />
      );

    case 'collaborationUpload':
      return (
        <CollabAgreementStep
          license={license}
          stateName={state?.fullName ?? ''}
          onChangePracticeType={showStateExhibitOption ? () => setStep('stateExhibitQuestion') : undefined}
          onBack={() => setStep('collaborationQuestion')}
          onComplete={() => setStep('collaborationSubmitted')}
        />
      );

    case 'collaborationSubmitted':
      return (
        <FinalStep
          icon={<CheckCircle color="success" />}
          title="Submitted for review"
          description={`We're reviewing your ${state?.fullName ?? 'your state'} collaborative agreement. We'll email you when you're cleared to see patients — usually within 2 business days.`}
          actionLabel="Back to Practice States"
          onAction={goToPracticeStates}
        />
      );

    case 'needMatch':
      return (
        <FinalStep
          icon={<Schedule color="primary" />}
          title="We'll take it from here"
          description="BetterHelp will pair you with a supervising physician. You can head back to Practice States — we'll let you know once you're matched."
          actionLabel="Back to Practice States"
          onAction={goToPracticeStates}
        />
      );

    case 'exhibitSign':
      return exhibitTemplate ? (
        <ExhibitSignatureStep
          providerId={license.userId}
          license={license}
          state={state}
          exhibitTemplate={exhibitTemplate}
          onBack={() => (requiresCollaborativeAgreement ? setStep('stateExhibitQuestion') : goToPracticeStates())}
          onComplete={expireAt => {
            setExhibitExpiresAt(expireAt);
            setStep('exhibitSigned');
          }}
        />
      ) : null;

    case 'exhibitSigned':
      return (
        <FinalStep
          icon={<CheckCircle color="success" />}
          title={`You're live in ${state?.fullName ?? 'your state'}`}
          description={`You can now see patients in ${state?.fullName ?? 'your state'}. Your signed state exhibit is on file${
            exhibitExpiresAt ? ` and expires ${CalendarDateService.format(exhibitExpiresAt, DateFormats['MMMM D, YYYY'])}` : ''
          } — we'll remind you to renew.`}
          actionLabel="Back to Practice States"
          onAction={goToPracticeStates}
        />
      );

    default:
      return null;
  }
};

const PracticeStateSetupContent = ({ stateId }: { stateId: string }) => {
  const me = useCurrentuser();
  const { locked: goLiveLocked, isLoading } = useGoLiveLocked();
  const { data: licenses } = useProviderLicensesStatusQuery(me.id, { suspense: true });
  const { data: matchRequestsByLicenseId } = useProviderCollaborativeMatchRequestsQuery(me.id);
  const license = licenses?.find(l => l.stateId === stateId);
  const matchRequested = license ? matchRequestsByLicenseId?.[license.id]?.collaborativePhysicianMatchRequested ?? false : false;

  if (isLoading) {
    return <Loader py={8} />;
  }

  if (goLiveLocked || !license) {
    return <Redirect to={ROUTING_CONFIG.practiceStates} />;
  }

  return <PracticeStateSetupWizard license={license} matchRequested={matchRequested} />;
};

export const PracticeStateSetupPage = () => {
  const { stateId } = practiceStateSetupRoute.useParams();
  const history = useHistory();
  const { data: state } = useStateQuery(stateId);

  return (
    <ContextualNavigationLayout
      headerProps={{
        leftButtonText: 'Back to Practice States',
        onLeftButtonClick: () => history.push(ROUTING_CONFIG.practiceStates),
        centerBlock: <Typography variant="h6" fontWeight={700} textAlign="center">Go live in {state?.fullName ?? ''}</Typography>,
      }}
    >
      <Suspense fallback={<Loader py={8} />}>
        <PracticeStateSetupContent stateId={stateId} />
      </Suspense>
    </ContextualNavigationLayout>
  );
};
